import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';

interface LandingPageProps {
  onGetStarted: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const theme = useTheme();

  const features = [
    {
      icon: <PsychologyIcon />,
      title: 'AI-Powered Analysis',
      description: 'Advanced AI analyzes your pitch with investor-grade insights',
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Adaptive Scoring',
      description: 'Scores improve with data volume through our learning system',
    },
    {
      icon: <SecurityIcon />,
      title: 'Privacy First',
      description: 'Your data is encrypted and only used with explicit consent',
    },
    {
      icon: <SpeedIcon />,
      title: 'Instant Results',
      description: 'Get comprehensive feedback in under 30 seconds',
    },
    {
      icon: <AnalyticsIcon />,
      title: 'Contextual Benchmarking',
      description: 'Compare against similar pitches in your industry and stage',
    },
    {
      icon: <AutoAwesomeIcon />,
      title: 'Actionable Feedback',
      description: 'Specific recommendations to improve your pitch deck',
    },
  ];

  const [realStats, setRealStats] = React.useState({
    pitchCount: 2,
    avgScore: 6.3
  });

  React.useEffect(() => {
    // Fetch real stats from backend
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/stats`)
      .then(res => res.json())
      .then(data => {
        if (data.total_pitches !== undefined && data.average_score !== undefined) {
          setRealStats({
            pitchCount: data.total_pitches,
            avgScore: data.average_score
          });
        }
      })
      .catch(err => console.log('Could not fetch stats:', err));
  }, []);

  const stats = [
    { label: 'Pitches Analyzed', value: realStats.pitchCount.toString(), trend: 'Growing database' },
    { label: 'Average Score', value: `${realStats.avgScore.toFixed(1)}/10`, trend: 'Industry benchmark' },
    { label: 'Time to Analysis', value: '<30s', trend: 'Lightning fast' },
    { label: 'AI Powered', value: 'Advanced', trend: 'Latest models' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', pt: 8, pb: 6 }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="Powered by Advanced AI Technology"
            color="primary"
            variant="outlined"
            sx={{ mb: 3 }}
          />
          <Typography
            component="h1"
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3,
            }}
          >
            Professional Pitch Deck Analysis
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            Get investor-grade feedback on your pitch deck in seconds. Our AI learns from every analysis to provide increasingly accurate scores and insights.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={onGetStarted}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              Analyze Your Pitch
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/sample-report"
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              View Sample Report
            </Button>
          </Stack>
        </Box>

        {/* Stats Section */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 3,
                }}
              >
                <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
                  {stat.value}
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {stat.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.trend}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Features Grid */}
        <Typography variant="h3" align="center" gutterBottom sx={{ mb: 5, fontWeight: 700 }}>
          Why Choose Our Platform?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    {React.cloneElement(feature.icon, { fontSize: 'large' })}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* How It Works Section */}
        <Box sx={{ mt: 10, mb: 8 }}>
          <Typography variant="h3" align="center" gutterBottom sx={{ mb: 5, fontWeight: 700 }}>
            How It Works
          </Typography>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 3,
                }}
              >
                <Typography variant="h1" color="primary" sx={{ fontWeight: 800, mb: 2 }}>
                  1
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Upload Your Deck
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PDF, PPTX, or paste text directly
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 3,
                }}
              >
                <Typography variant="h1" color="secondary" sx={{ fontWeight: 800, mb: 2, color: theme.palette.primary.main }}>
                  2
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  AI Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Advanced AI with contextual learning from similar pitches
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 3,
                }}
              >
                <Typography variant="h1" sx={{ fontWeight: 800, mb: 2, color: theme.palette.success.main }}>
                  3
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Get Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detailed scores, feedback, and improvements
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Trust Section */}
        <Box
          sx={{
            mt: 10,
            p: 6,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
            Trusted by Founders Worldwide
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Join thousands of entrepreneurs improving their pitch decks with data-driven insights
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {['Privacy-First Design', 'Encrypted Storage', 'Explicit Consent', 'Secure Infrastructure'].map((item) => (
              <Grid item key={item}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={item}
                  color="primary"
                  variant="outlined"
                  sx={{ borderWidth: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 10 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Perfect Your Pitch?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Get professional feedback in seconds. No credit card required.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onGetStarted}
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
            endIcon={<TrendingUpIcon />}
          >
            Start Free Analysis
          </Button>
        </Box>
      </Container>
    </Box>
  );
};